import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';

/**
 * ConfirmationModalDemoComponent
 * 
 * Componente de demostración que muestra todos los casos de uso
 * del ConfirmationModalComponent y ConfirmationService
 */
@Component({
    selector: 'app-confirmation-modal-demo',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatSnackBarModule
    ],
    templateUrl: './confirmation-modal-demo.component.html',
    styleUrls: ['./confirmation-modal-demo.component.scss']
})
export class ConfirmationModalDemoComponent {
    lastAction: string = 'Ninguna acción ejecutada aún';

    constructor(
        private confirmationService: ConfirmationService,
        private snackBar: MatSnackBar
    ) {}

    // ========================================
    // MÉTODOS PRECONFIGURADOS DEL SERVICIO
    // ========================================

    /**
     * Ejemplo 1: Eliminar elemento
     */
    testDelete(): void {
        this.confirmationService
            .confirmDelete(
                'el almacén "Warehouse Principal"',
                'Esta acción eliminará permanentemente el almacén y sus relaciones asociadas.'
            )
            .subscribe(confirmed => {
                this.handleResult('Eliminar', confirmed);
            });
    }

    /**
     * Ejemplo 2: Anular documento
     */
    testAnular(): void {
        this.confirmationService
            .confirmAnular('Factura #12345')
            .subscribe(confirmed => {
                this.handleResult('Anular documento', confirmed);
            });
    }

    /**
     * Ejemplo 3: Guardar cambios
     */
    testSave(): void {
        this.confirmationService
            .confirmSave('¿Desea guardar los cambios realizados en el formulario?')
            .subscribe(confirmed => {
                this.handleResult('Guardar cambios', confirmed);
            });
    }

    /**
     * Ejemplo 4: Descartar cambios
     */
    testDiscard(): void {
        this.confirmationService
            .confirmDiscard()
            .subscribe(confirmed => {
                this.handleResult('Descartar cambios', confirmed);
            });
    }

    /**
     * Ejemplo 5: Cerrar sesión
     */
    testLogout(): void {
        this.confirmationService
            .confirmLogout()
            .subscribe(confirmed => {
                this.handleResult('Cerrar sesión', confirmed);
            });
    }

    /**
     * Ejemplo 6: Enviar notificación
     */
    testSendNotification(): void {
        this.confirmationService
            .confirmSendNotification(25)
            .subscribe(confirmed => {
                this.handleResult('Enviar notificación', confirmed);
            });
    }

    /**
     * Ejemplo 7: Exportar datos
     */
    testExport(): void {
        this.confirmationService
            .confirmExport('Reportes de Ventas')
            .subscribe(confirmed => {
                this.handleResult('Exportar datos', confirmed);
            });
    }

    /**
     * Ejemplo 8: Procesar lote
     */
    testBatchProcess(): void {
        this.confirmationService
            .confirmBatchProcess(150)
            .subscribe(confirmed => {
                this.handleResult('Procesar lote', confirmed);
            });
    }

    /**
     * Ejemplo 9: Restaurar valores
     */
    testReset(): void {
        this.confirmationService
            .confirmReset()
            .subscribe(confirmed => {
                this.handleResult('Restaurar valores', confirmed);
            });
    }

    /**
     * Ejemplo 10: Activar elemento
     */
    testActivate(): void {
        this.confirmationService
            .confirmToggleStatus('el usuario "Juan Pérez"', true)
            .subscribe(confirmed => {
                this.handleResult('Activar', confirmed);
            });
    }

    /**
     * Ejemplo 11: Desactivar elemento
     */
    testDeactivate(): void {
        this.confirmationService
            .confirmToggleStatus('el usuario "Juan Pérez"', false)
            .subscribe(confirmed => {
                this.handleResult('Desactivar', confirmed);
            });
    }

    /**
     * Ejemplo 12: Modal de advertencia
     */
    testWarning(): void {
        this.confirmationService
            .warning(
                'Advertencia de Seguridad',
                'El sistema detectó un intento de acceso no autorizado. Se recomienda cambiar su contraseña.'
            )
            .subscribe(confirmed => {
                this.handleResult('Advertencia', confirmed);
            });
    }

    /**
     * Ejemplo 13: Modal de información
     */
    testInfo(): void {
        this.confirmationService
            .info(
                'Actualización Completada',
                'El sistema se ha actualizado correctamente a la versión 2.0.0'
            )
            .subscribe(confirmed => {
                this.handleResult('Información', confirmed);
            });
    }

    // ========================================
    // MODALES PERSONALIZADOS
    // ========================================

    /**
     * Ejemplo 14: Modal personalizado - Tipo DANGER
     */
    testCustomDanger(): void {
        this.confirmationService
            .confirm({
                title: 'Acción Crítica',
                description: 'Esta acción es <strong>irreversible</strong> y puede afectar a múltiples usuarios.',
                type: ConfirmationModalType.DANGER,
                confirmButtonText: 'Ejecutar',
                cancelButtonText: 'Cancelar',
                descriptionIcon: 'warning'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo DANGER', confirmed);
            });
    }

    /**
     * Ejemplo 15: Modal personalizado - Tipo SUCCESS
     */
    testCustomNormal(): void {
        this.confirmationService
            .confirm({
                title: 'Confirmar Operación',
                description: '¿Desea continuar con esta operación?',
                type: ConfirmationModalType.SUCCESS,
                confirmButtonText: 'Continuar',
                cancelButtonText: 'Cancelar',
                descriptionIcon: 'check_circle'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo SUCCESS', confirmed);
            });
    }

    /**
     * Ejemplo 16: Modal con ancho personalizado
     */
    testCustomWidth(): void {
        this.confirmationService
            .confirm({
                title: 'Modal con Ancho Personalizado',
                description: `
                    <div>
                        <p class="mb-2">Este modal tiene un ancho de 600px.</p>
                        <p>Útil para mostrar más contenido sin que el modal se vea muy pequeño.</p>
                    </div>
                `,
                confirmButtonText: 'Aceptar',
                cancelButtonText: 'Cerrar',
                descriptionIcon: 'aspect_ratio',
                descriptionColor: 'text-gray-700',
                customWidth: '600px'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal ancho personalizado', confirmed);
            });
    }

    /**
     * Ejemplo 17: Modal con HTML enriquecido
     */
    testHtmlContent(): void {
        this.confirmationService
            .confirm({
                title: 'Detalles de la Operación',
                description: `
                    <div>
                        <p class="font-semibold mb-2">Se realizarán las siguientes acciones:</p>
                        <ul class="list-disc ml-5 mb-3">
                            <li>Actualizar 50 registros</li>
                            <li>Enviar 10 notificaciones</li>
                            <li>Generar reporte PDF</li>
                        </ul>
                        <p class="text-sm text-orange-600">
                            <strong>Nota:</strong> Esta operación puede tardar varios minutos.
                        </p>
                    </div>
                `,
                confirmButtonText: 'Procesar',
                cancelButtonText: 'Cancelar',
                descriptionIcon: 'playlist_add_check',
                descriptionColor: 'text-gray-700',
                customWidth: '500px'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal con HTML', confirmed);
            });
    }

    /**
     * Ejemplo 18: Sin ícono
     */
    testNoIcon(): void {
        this.confirmationService
            .confirm({
                title: 'Modal Simple',
                description: 'Este modal no tiene ícono, solo título y descripción.',
                confirmButtonText: 'Aceptar',
                cancelButtonText: 'Cancelar'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal sin ícono', confirmed);
            });
    }

    /**
     * Ejemplo 19: Tipo DEFAULT (sin especificar type)
     */
    testDefaultType(): void {
        this.confirmationService
            .confirm({
                title: 'Modal por Defecto',
                description: 'Este modal no especifica un tipo, por lo que usa el tipo DEFAULT automáticamente (ícono gris).',
                confirmButtonText: 'Aceptar',
                cancelButtonText: 'Cancelar',
                descriptionIcon: 'help_outline'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo DEFAULT', confirmed);
            });
    }

    /**
     * Ejemplo 20: Tipo SUCCESS explícito
     */
    testSuccessType(): void {
        this.confirmationService
            .confirm({
                title: 'Operación Exitosa',
                description: 'La operación se completó correctamente. ¿Desea continuar con el siguiente paso?',
                type: ConfirmationModalType.SUCCESS,
                confirmButtonText: 'Continuar',
                cancelButtonText: 'Finalizar',
                descriptionIcon: 'check_circle'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo SUCCESS', confirmed);
            });
    }

    /**
     * Ejemplo 21: Tipo WARNING explícito
     */
    testWarningType(): void {
        this.confirmationService
            .confirm({
                title: 'Advertencia Importante',
                description: 'Esta acción modificará configuraciones críticas del sistema. Se recomienda precaución.',
                type: ConfirmationModalType.WARNING,
                confirmButtonText: 'Proceder',
                cancelButtonText: 'Cancelar',
                descriptionIcon: 'report_problem'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo WARNING', confirmed);
            });
    }

    /**
     * Ejemplo 22: Tipo INFO explícito
     */
    testInfoType(): void {
        this.confirmationService
            .confirm({
                title: 'Información del Sistema',
                description: 'Hay una nueva actualización disponible. ¿Desea instalarla ahora?',
                type: ConfirmationModalType.INFO,
                confirmButtonText: 'Instalar',
                cancelButtonText: 'Más Tarde',
                descriptionIcon: 'info'
            })
            .subscribe(confirmed => {
                this.handleResult('Modal tipo INFO', confirmed);
            });
    }

    // ========================================
    // UTILIDADES
    // ========================================

    /**
     * Maneja el resultado de las confirmaciones
     */
    private handleResult(action: string, confirmed: boolean): void {
        if (confirmed) {
            this.lastAction = `✅ ${action}: CONFIRMADO`;
            this.snackBar.open(`${action} confirmado`, 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar']
            });
        } else {
            this.lastAction = `❌ ${action}: CANCELADO`;
            this.snackBar.open(`${action} cancelado`, 'Cerrar', {
                duration: 3000,
                panelClass: ['info-snackbar']
            });
        }
    }
}

